import { addDays, addWeeks, addYears, isAfter, isBefore, isToday, startOfToday } from 'date-fns'

import { ApiBusinessUnit, ApiClient } from '../../../../../api/_legacy/generated-api'
import { Fee } from '../../../types'
import { Errors, ValidatorRules } from '../../../util/validator'
import { MaturityDateType, Note } from '../types'

export interface NewNoteState {
  canProceed: boolean
  client?: ApiClient
  clients: ApiClient[]
  counterpartyIds: string[]
  currentBusinessUnits: ApiBusinessUnit[]
  errors: Errors
  fees?: Fee[]
  isCollateralSet: boolean
  maturityDateType?: MaturityDateType
  note: Note
  noteNames: string[]
  tenant?: string
}

export const newNoteRules: ValidatorRules<NewNoteState> = {
  client: [
    (state: NewNoteState) => ({
      isValid: state.note.client !== undefined,
      message: 'Please select a client'
    })
  ],
  businessUnit: [
    (state: NewNoteState) => ({
      isValid: state.note.businessUnit !== undefined,
      message: 'Please select a business unit'
    })
  ],
  noteName: [
    (state: NewNoteState) => ({
      isValid: state.note.noteName !== '',
      message: 'Please provide a note name'
    }),
    (state: NewNoteState) => ({
      isValid: !state.noteNames.includes(state.note.noteName.trim()),
      message: 'This note name is already in use'
    })
  ],
  isCollateralForLoan: [
    (state: NewNoteState) => ({
      isValid: state.isCollateralSet,
      message: 'Please choose if there is collateral for loan'
    })
  ],
  startDate: [
    (state: NewNoteState) => {
      if (!state.note.startDate) {
        return {
          isValid: false,
          message: 'You must set a start date'
        }
      }

      const startDate = new Date(state.note.startDate)
      const isValid = isToday(startDate) || isAfter(startDate, startOfToday())

      return {
        isValid,
        message: 'The start date must be today or after today'
      }
    },
    (state: NewNoteState) => {
      if (!state.note.startDate) {
        return {
          isValid: false,
          message: 'You must set a start date'
        }
      }

      const startDate = new Date(state.note.startDate)
      const oneYearFromToday = addYears(startOfToday(), 1)
      const oneYearAndOneDayFromToday = addDays(oneYearFromToday, 1)
      const isValid = isBefore(startDate, oneYearAndOneDayFromToday)

      return {
        isValid,
        message: 'The start date must be within one year of today'
      }
    }
  ],
  endDate: [
    (state: NewNoteState) => {
      const isValid = state.maturityDateType !== undefined

      return {
        isValid,
        message: 'You must choose a perpetual or specific maturity date'
      }
    },
    (state: NewNoteState) => {
      // this rule needs the start date, so it needs to be checked first
      if (!state.note.startDate) {
        return {
          isValid: false,
          message: 'You must set a start date'
        }
      }

      if (state.maturityDateType === 'perpetual') {
        return {
          isValid: true
        }
      }

      if (!state.note.endDate) {
        return {
          isValid: false,
          message: 'You must set a maturity date'
        }
      }

      const startDate = new Date(state.note.startDate)
      const endDate = new Date(state.note.endDate)
      const oneWeekAfterStartDate = addWeeks(startDate, 1)
      const isValid = isAfter(endDate, oneWeekAfterStartDate)

      return {
        isValid,
        message: 'The maturity date has to be a week or more after the start date'
      }
    }
  ]
}
